import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Linkedin, Instagram, Download, ArrowRight } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

function Reveal({ children, delay = 0, className = '' }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={`${isVisible ? 'reveal-visible' : 'reveal-hidden'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ─── Contact Hero ─── */
function ContactHero() {
  return (
    <section className="bg-brand-navy py-20 lg:py-28" data-testid="contact-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-4 font-source-sans">
              Contact
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-lexend font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6">
              Talk to a Senior{' '}
              <span className="text-brand-orange">Specialist</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-source-sans">
              Whether you are starting your source-to-pay journey or optimising
              an existing environment - the right conversation starts here.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Contact Details ─── */
function ContactDetails() {
  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="contact-form-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl text-brand-navy mb-3">
              Get in Touch
            </h2>
            <p className="text-slate-600 font-source-sans text-base mb-10 leading-relaxed">
              Reach us directly - our senior specialists respond within one business day.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
            {/* Email */}
            <Reveal delay={60}>
              <a
                href="mailto:info@imane.in"
                className="flex items-start gap-4 bg-brand-bg rounded-xl p-6 border border-brand-border hover:border-brand-orange transition-colors group"
                data-testid="contact-detail-email"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-orange-light flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-brand-orange" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 font-source-sans">Email</p>
                  <p className="text-brand-navy font-semibold font-source-sans group-hover:text-brand-orange transition-colors">
                    info@imane.in
                  </p>
                </div>
              </a>
            </Reveal>

            {/* WhatsApp / Phone */}
            <Reveal delay={100}>
              <a
                href="https://wa.me/919637625311"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 bg-brand-bg rounded-xl p-6 border border-brand-border hover:border-brand-orange transition-colors group"
                data-testid="contact-detail-phone"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-orange-light flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-brand-orange" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 font-source-sans">WhatsApp / Phone</p>
                  <p className="text-brand-navy font-semibold font-source-sans group-hover:text-brand-orange transition-colors">
                    +91 96376 25311
                  </p>
                </div>
              </a>
            </Reveal>

            {/* LinkedIn */}
            <Reveal delay={140}>
              <a
                href="https://www.linkedin.com/company/imane-labs"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 bg-brand-bg rounded-xl p-6 border border-brand-border hover:border-brand-orange transition-colors group"
                data-testid="contact-detail-linkedin"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-orange-light flex items-center justify-center flex-shrink-0">
                  <Linkedin size={20} className="text-brand-orange" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 font-source-sans">LinkedIn</p>
                  <p className="text-brand-navy font-semibold font-source-sans group-hover:text-brand-orange transition-colors">
                    linkedin.com/company/imane-labs
                  </p>
                </div>
              </a>
            </Reveal>

            {/* Instagram */}
            <Reveal delay={180}>
              <a
                href="https://www.instagram.com/imane_labs/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 bg-brand-bg rounded-xl p-6 border border-brand-border hover:border-brand-orange transition-colors group"
                data-testid="contact-detail-instagram"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-orange-light flex items-center justify-center flex-shrink-0">
                  <Instagram size={20} className="text-brand-orange" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 font-source-sans">Instagram</p>
                  <p className="text-brand-navy font-semibold font-source-sans group-hover:text-brand-orange transition-colors">
                    @imane_labs
                  </p>
                </div>
              </a>
            </Reveal>
          </div>

          {/* Office Address card */}
          <Reveal delay={220}>
            <div
              className="rounded-xl border border-brand-border bg-brand-bg p-6 flex items-start gap-4"
              data-testid="office-location-card"
            >
              <div className="w-11 h-11 rounded-lg bg-brand-orange-light flex items-center justify-center flex-shrink-0">
                <MapPin size={20} className="text-brand-orange" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 font-source-sans">Our Office</p>
                <p className="text-brand-navy font-semibold font-source-sans">
                  Kalpataru Harmony, Wakad,<br />Pune, Maharashtra 411057, India
                </p>
                <a
                  href="https://maps.google.com/?q=Kalpataru+Harmony+Wakad+Pune"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-brand-orange text-sm font-semibold font-source-sans hover:underline mt-2"
                >
                  View on Google Maps <ArrowRight size={13} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Capability Deck Download ─── */
function CapabilityDeck() {
  return (
    <section className="py-16 bg-white border-t border-brand-border" data-testid="capability-deck">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <h2 className="font-lexend font-bold text-2xl lg:text-3xl text-brand-navy mb-4">
              Download Our Capability Deck
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-slate-600 font-source-sans text-base leading-relaxed mb-6">
              A concise overview of our services, approach, and case study highlights - designed
              for sharing with procurement and technology leadership.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex flex-col items-start gap-3">
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="btn-orange-outline inline-flex items-center gap-2"
                data-testid="capability-deck-download-btn"
              >
                <Download size={17} />
                Download Capability Deck (PDF)
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <Helmet>
        <title>Contact iMANE | Talk to a Senior Procurement Specialist | Mane Labs LLP</title>
        <meta name="description" content="Contact iMANE (Mane Labs LLP) for procurement and supply chain transformation consulting. Email info@imane.in, WhatsApp +91 96376 25311. Office in Wakad, Pune, Maharashtra." />
        <meta name="keywords" content="contact iMANE, iMANE Pune, mane labs contact, procurement consultant contact India, iMANE phone, iMANE email" />
        <link rel="canonical" href="https://www.imane.in/contact" />
        <meta property="og:title" content="Contact iMANE | Talk to a Senior Procurement Specialist" />
        <meta property="og:description" content="Reach iMANE (Mane Labs LLP) via email, WhatsApp, or LinkedIn. Pune office. Senior specialists respond within one business day." />
        <meta property="og:url" content="https://www.imane.in/contact" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "iMANE - Mane Labs LLP",
          "telephone": "+919637625311",
          "email": "info@imane.in",
          "address": {"@type": "PostalAddress", "streetAddress": "Kalpataru Harmony, Wakad", "addressLocality": "Pune", "addressRegion": "Maharashtra", "postalCode": "411057", "addressCountry": "IN"},
          "url": "https://www.imane.in/contact",
          "sameAs": ["https://www.linkedin.com/company/imane-labs", "https://www.instagram.com/imane_labs/"]
        })}</script>
      </Helmet>
      <ContactHero />
      <ContactDetails />
      <CapabilityDeck />
    </div>
  );
}
